/**
 * Gamma Specifications Block
 * Displays variant-specific specifications.
 * 
 * Updated for high-variant products (250+ variants):
 * - Receives specs from configurator's variant:changed event (when available)
 * - Falls back to preloaded JSON for variants within first 250
 * - No additional API calls needed - uses data from configurator's fetch
 */

class GammaSpecifications extends HTMLElement {
    constructor() {
        super();
        this.currentVariantId = null;
        this.variantSpecs = {};  // Preloaded specs for first 250 variants
        this.specsCache = {};    // Cache for specs received from configurator
    }

    connectedCallback() {
        this.currentVariantId = this.dataset.currentVariantId;
        this.sectionId = this.dataset.sectionId;
        this.productUrl = this.dataset.productUrl;

        // Load preloaded variant specifications (first 250 variants)
        const specsScript = document.querySelector('script[data-gamma-variant-specs]');
        if (specsScript) {
            try {
                this.variantSpecs = JSON.parse(specsScript.textContent);
            } catch (e) {
                console.error('GammaSpecifications: Failed to parse preloaded specs', e);
            }
        }

        // Cache initial specs
        const currentSpecsScript = document.querySelector('script[data-gamma-current-specs]');
        if (currentSpecsScript && this.currentVariantId) {
            try {
                const currentSpecs = JSON.parse(currentSpecsScript.textContent);
                if (currentSpecs && Object.keys(currentSpecs).length > 0) {
                    this.specsCache[this.currentVariantId] = currentSpecs;
                }
            } catch (e) {
                console.warn('GammaSpecifications: Could not parse current specs', e);
            }
        }

        // Listen for variant changes from configurator
        document.addEventListener('variant:changed', (event) => {
            const variant = event.detail?.variant;
            const specs = event.detail?.specifications;

            if (variant?.id) {
                // If specs were passed in the event, cache them
                if (specs && Object.keys(specs).length > 0) {
                    this.specsCache[variant.id.toString()] = specs;
                }
                this.updateSpecifications(variant.id, specs);
            }
        });
    }

    updateSpecifications(variantId, providedSpecs = null) {
        const variantIdStr = variantId.toString();

        if (variantIdStr === this.currentVariantId?.toString()) return;
        this.currentVariantId = variantIdStr;

        const container = this.querySelector('[data-specs-content]');
        if (!container) return;

        // Priority: 1. Provided specs, 2. Cached specs, 3. Preloaded specs
        let specs = providedSpecs;

        if (!specs || Object.keys(specs).length === 0) {
            specs = this.specsCache[variantIdStr];
        }

        if (!specs || Object.keys(specs).length === 0) {
            specs = this.variantSpecs[variantIdStr];
        }

        if (specs && Object.keys(specs).length > 0) {
            container.innerHTML = `
        <table class="gamma-specifications__table">
          <tbody>
            ${Object.entries(specs).map(([key, value]) => `
              <tr class="gamma-specifications__row">
                <th class="gamma-specifications__key">${this.escapeHtml(key)}</th>
                <td class="gamma-specifications__value">${this.escapeHtml(String(value))}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      `;
        } else {
            container.innerHTML = '<p class="gamma-specifications__empty">No specifications available.</p>';
        }
    }

    escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }
}

if (!customElements.get('gamma-specifications')) {
    customElements.define('gamma-specifications', GammaSpecifications);
}
